import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  activeProcessSF,
  getActiveProcessKey,
  handleProcess,
  IRydeProcess,
  Log,
  RydeProcessTitle,
  RydeUiUnit,
  TAddressedFieldsValueOnCompleted,
  useProcesses,
} from "@ryde/shared";

interface IUseWatchBulkRideSendSMSProps {
  entity: RydeUiUnit;
  addressedFieldsValueOnCompleted?: TAddressedFieldsValueOnCompleted;
}

export const useWatchBulkRideSendSMS = ({
  entity,
  addressedFieldsValueOnCompleted,
}: IUseWatchBulkRideSendSMSProps) => {
  const { t } = useTranslation();
  const bulkSendSMSProcessKey = getActiveProcessKey(
    RydeProcessTitle.BulkRideSendSMS,
    entity,
  );
  const bulkSendSMSProcess = useRecoilValue(
    activeProcessSF(bulkSendSMSProcessKey),
  );
  const { stopProcessPolling } = useProcesses();

  const onComplete = useCallback(
    (process: IRydeProcess) => {
      Log.info("BulkRideSendSMS process completed", {
        processId: process.id,
        entity,
      });
      addressedFieldsValueOnCompleted?.(process);
    },
    [addressedFieldsValueOnCompleted, entity],
  );

  const onFailed = useCallback(
    (process: IRydeProcess) => {
      Log.error("BulkRideSendSMS process failed", {
        processId: process.id,
        entity,
        errors: process.errors,
      });
    },
    [entity],
  );

  useEffect(() => {
    handleProcess({
      entity,
      process: bulkSendSMSProcess,
      onComplete,
      onFail: onFailed,
      translationFunction: t,
      stopProcessPolling,
      processTitle: RydeProcessTitle.BulkRideSendSMS,
      toastSummaryOnComplete: true,
    });
  }, [bulkSendSMSProcess, entity, onComplete, onFailed, stopProcessPolling, t]);
};
